/**
 * Page /comptabilite — Brique comptable (service comptable cloisonné).
 *
 * Onglets : Vue d'ensemble · Encaissements · Dépôts banque · Postes de ventilation.
 * Accent ardoise gris-bleu. Encaissement manuel avec ventilation analytique
 * (répartition montant sur des postes ; le total doit égaler le montant).
 * Dépôt en banque générant un bordereau.
 *
 * Couleurs par secteur d'origine sur les lignes (cantine rouge, salle bleu,
 * asso violet, grc teal, rh orange, divers ambre).
 */

import { useEffect, useState, useCallback } from 'react';
import { AppHeader } from '@/components/AppHeader';
import { Card, Button, Badge, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { PresenceSimplePage } from '@/pages/shared/PresenceSimplePage';
import { NdfTypesFraisPanel } from './NdfTypesFraisPanel';
import { NdfComptaPanel } from './NdfComptaPanel';
import { FactureManuellePanel } from './FactureManuellePanel';
import { NdfTiersPanel } from './NdfTiersPanel';
import { TemplatesFacturePanel } from './TemplatesFacturePanel';
import { DecaissementsPanel } from './DecaissementsPanel';
import { BudgetsPanel } from './BudgetsPanel';
import { FacturesRecuesPanel } from './FacturesRecuesPanel';
import { LivrePanel } from './LivrePanel';
import { ComptaNav, type ComptaTab } from './ComptaNav';
import { ReglagesContenu } from '@/pages/reglages/ReglagesBriquePage';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import type { ComptaDashboardData, ComptaEncaissement, ComptaPoste, ComptaDepot, ComptaJournalResult, FactureAEncaisser } from '@/lib/api';
import {
  Calculator, Plus, AlertCircle, FileDown, Download, FileText, ArrowRight,
} from 'lucide-react';

const ACCENT = 'oklch(var(--c-compta))'; // cyan comptabilité (aligné brique)

const SOURCE_META: Record<string, { label: string; color: string }> = {
  cantine: { label: 'Scolaire', color: 'oklch(0.70 0.20 25)' },
  petite_enfance: { label: 'Petite enfance', color: 'oklch(0.72 0.16 350)' },
  salle: { label: 'Salles', color: 'oklch(0.68 0.16 240)' },
  asso: { label: 'Asso', color: 'oklch(0.68 0.19 290)' },
  grc: { label: 'GRC', color: 'oklch(0.70 0.13 175)' },
  rh: { label: 'RH', color: 'oklch(0.76 0.16 60)' },
  tech: { label: 'Technique', color: 'oklch(0.68 0.16 240)' },
  urba: { label: 'Urbanisme', color: 'oklch(0.70 0.15 145)' },
  divers: { label: 'Divers', color: 'oklch(0.80 0.16 75)' },
};
const MOYEN_LABEL: Record<string, string> = {
  cheque: 'Chèque', espece: 'Espèces', virement: 'Virement', cb_payfip: 'CB', sepa: 'SEPA',
};
const SOURCES = ['cantine', 'petite_enfance', 'salle', 'asso', 'grc', 'rh', 'tech', 'urba', 'divers'] as const;
const MOYENS = ['cheque', 'espece', 'virement', 'cb_payfip', 'sepa'] as const;

function eur(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' €';
}
function srcMeta(s: string) {
  return SOURCE_META[s] ?? SOURCE_META.divers!;
}

/** Télécharge un Blob sous un nom de fichier donné. */
async function downloadBlob(getBlob: () => Promise<Blob>, filename: string) {
  const blob = await getBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type Tab = ComptaTab;

export default function ComptabilitePage() {
  const { confirm, dialogElement } = useConfirmDialog();
  const { user } = useAuth();
  // Un agent du service comptable a un accès restreint (lecture + saisie
  // d'encaissement) : on n'affiche que les onglets qu'il peut réellement
  // utiliser — cohérent avec le cloisonnement appliqué aux autres briques.
  const estGestionCompta = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';
  // ?onglet=… ouvre directement un onglet (ex. les réglages depuis la page Mairie).
  const [tab, setTab] = useState<Tab>(() => (new URLSearchParams(window.location.search).get('onglet') as Tab) || 'ensemble');
  // Si un agent atterrit sur un onglet réservé (état résiduel), on le ramène sur
  // la vue d'ensemble.
  useEffect(() => {
    // « presence » est ouvert à TOUS les rôles, agent compris : voir son propre
    // planning et celui de son équipe n'est pas un acte de gestion comptable.
    // C'était le seul service où l'agent n'avait aucun accès à son planning —
    // il retombait ici sur « ensemble » (retour mairie, 24/07).
    if (!estGestionCompta && !['ensemble', 'encaissements', 'journal', 'factures', 'presence'].includes(tab)) setTab('ensemble');
  }, [estGestionCompta, tab]);
  const [error, setError] = useState<string | null>(null);

  const [dash, setDash] = useState<ComptaDashboardData | null>(null);
  const [encs, setEncs] = useState<ComptaEncaissement[]>([]);
  const [postes, setPostes] = useState<ComptaPoste[]>([]);
  const [depots, setDepots] = useState<ComptaDepot[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, e, p, dep] = await Promise.all([
        api.comptabilite.dashboard(),
        api.comptabilite.encaissements.list(),
        api.comptabilite.postes.list(),
        api.comptabilite.depots.list(),
      ]);
      setDash(d);
      setEncs(e.encaissements);
      setPostes(p.postes);
      setDepots(dep.depots);
    } catch {
      setError('Impossible de charger la comptabilité.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Comptabilité" accent="compta" />

      <main id="main-content" className="mx-auto max-w-[1400px] px-4 py-5">
        <div className="mb-4 flex items-center gap-3">
          <LogoMark icon={Calculator} accent="compta" size="lg" />
          <div>
            <SectionEyebrow as="div">Régie & flux financiers</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              Comptabilité
            </h1>
          </div>
          <Badge variant="neutral" size="sm" className="ml-auto">Service comptable · cloisonné</Badge>
        </div>

        {/* Onglets regroupés (5 entrées, menus déroulants façon GRC) */}
        <ComptaNav tab={tab} setTab={setTab} estGestionCompta={estGestionCompta} />

        {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

        {loading ? (
          <Card variant="glass" padded>
            <div className="py-10 text-center">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-t-transparent" style={{ borderColor: ACCENT, borderTopColor: 'transparent' }} role="status" aria-label="Chargement" />
            </div>
          </Card>
        ) : (
          <>
            {tab === 'ensemble' && dash && <VueEnsemble dash={dash} setTab={setTab} />}
            {tab === 'encaissements' && (
              <Encaissements encs={encs} postes={postes} onChange={load} setError={setError} />
            )}
            {tab === 'factures' && <FactureManuellePanel />}
            {tab === 'roles_asap' && <RolesAsapPanel setTab={setTab} />}
            {tab === 'modeles' && <TemplatesFacturePanel />}
            {tab === 'decaissements' && <DecaissementsPanel postes={postes} />}
            {tab === 'budgets' && <BudgetsPanel estGestion={estGestionCompta} />}
            {tab === 'factures_recues' && <FacturesRecuesPanel />}
            {tab === 'livre' && <LivrePanel />}
            {tab === 'depots' && (
              <Depots encs={encs} depots={depots} onChange={load} setError={setError} />
            )}
            {tab === 'journal' && <Journal setError={setError} />}
            {tab === 'postes' && (
              <Postes postes={postes} onChange={load} setError={setError} />
            )}
            {tab === 'presence' && (
              <PresenceSimplePage planningApi={api.comptabilite.planning} accent="comptable" service="comptable" />
            )}
            {tab === 'ndf' && <NdfComptaPanel />}
            {tab === 'types_frais' && <NdfTypesFraisPanel />}
            {tab === 'tiers' && <NdfTiersPanel />}
            {tab === 'reglages' && <ReglagesContenu brique="comptabilite" />}
          </>
        )}
      </main>

      {dialogElement}
    </div>
  );
}

// ── Rôle collectif ASAP ──────────────────────────────────────────────────────
// Regroupe plusieurs factures scolaires émises d'un mois (ex. cantine) en un
// seul flux ASAP ORMC (format DGFiP), au lieu d'un titre par famille. On liste
// les factures du mois choisi, on coche celles à inclure, puis on exporte le
// rôle XML (validé XSD côté serveur).

type RoleFacture = {
  id: string; numero: string; familleNom: string; service: string;
  periodeDebut: string | null; periodeFin: string | null; montantTtc: number; statut: string;
};

function moisCourant(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const SERVICE_LABEL: Record<string, string> = {
  cantine: 'Cantine', periscolaire: 'Périscolaire', alsh: 'ALSH', multi: 'Multi-services',
};

function RolesAsapPanel({ setTab }: { setTab: (t: Tab) => void }) {
  const [mois, setMois] = useState(moisCourant());
  const [factures, setFactures] = useState<RoleFacture[]>([]);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [charge, setCharge] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { factures: f } = await api.scolaire.facturation.roleAsapPreview(mois);
      setFactures(f);
      setSelection(new Set(f.map((x) => x.id))); // tout coché par défaut
      setCharge(true);
    } catch (e) {
      setErr(e instanceof ApiException ? e.payload.error : 'Impossible de charger les factures du mois.');
      setFactures([]);
      setSelection(new Set());
    } finally {
      setLoading(false);
    }
  }, [mois]);

  function toggle(id: string) {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleTous() {
    setSelection((prev) => (prev.size === factures.length ? new Set() : new Set(factures.map((f) => f.id))));
  }

  async function exporter() {
    if (selection.size === 0) return;
    setExporting(true);
    setErr(null);
    try {
      await api.scolaire.facturation.roleAsapExport(mois, [...selection]);
    } catch (e) {
      setErr(e instanceof ApiException ? e.payload.error : 'Export du rôle impossible.');
    } finally {
      setExporting(false);
    }
  }

  const total = factures.filter((f) => selection.has(f.id)).reduce((s, f) => s + f.montantTtc, 0);

  return (
    <div className="space-y-4">
      <Card variant="glass" padded>
        <div className="flex items-start gap-3">
          <LogoMark icon={FileText} accent="compta" size="md" />
          <div className="flex-1">
            <SectionEyebrow as="div">Recettes · rôle collectif</SectionEyebrow>
            <h2 className="mt-0.5 font-display text-lg font-semibold text-ink">Rôle collectif ASAP ORMC</h2>
            <p className="mt-2 text-sm text-ink-muted">
              Regroupe les factures scolaires émises d'un mois (cantine, périscolaire, ALSH)
              en un seul flux ASAP ORMC conforme au schéma DGFiP, au lieu d'un titre par famille.
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="role-mois" className="block text-xs font-medium text-ink-muted">Mois</label>
                <input
                  id="role-mois"
                  type="month"
                  value={mois}
                  onChange={(e) => { setMois(e.target.value); setCharge(false); }}
                  className="mt-1 rounded-r-md border border-border bg-surface px-3 py-1.5 text-sm text-ink"
                />
              </div>
              <Button variant="secondary" size="sm" onClick={() => void charger()} disabled={loading}>
                {loading ? 'Chargement…' : 'Charger les factures'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}

      {charge && (
        factures.length === 0 ? (
          <Card variant="glass" padded>
            <p className="text-sm text-ink-muted">
              Aucune facture scolaire émise pour {mois}. Émets d'abord les factures depuis la brique
              Scolaire, ou vérifie le mois choisi.
            </p>
            <div className="mt-3">
              <Button variant="ghost" size="sm" onClick={() => setTab('factures')}>Voir les factures émises</Button>
            </div>
          </Card>
        ) : (
          <Card variant="glass" padded>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-ink-muted">
                {factures.length} facture{factures.length > 1 ? 's' : ''} émise{factures.length > 1 ? 's' : ''} · {selection.size} sélectionnée{selection.size > 1 ? 's' : ''}
              </div>
              <button type="button" onClick={toggleTous} className="text-xs font-medium text-ink-muted underline hover:text-ink">
                {selection.size === factures.length ? 'Tout décocher' : 'Tout cocher'}
              </button>
            </div>

            <div className="divide-y divide-border overflow-hidden rounded-r-md border border-border">
              {factures.map((f) => (
                <label key={f.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-surface/40">
                  <input
                    type="checkbox"
                    checked={selection.has(f.id)}
                    onChange={() => toggle(f.id)}
                    className="h-4 w-4 accent-current"
                    style={{ accentColor: ACCENT }}
                  />
                  <span className="min-w-[130px] font-mono text-xs text-ink-muted">{f.numero}</span>
                  <span className="flex-1 text-ink">{f.familleNom}</span>
                  <Badge variant="neutral" size="sm">{SERVICE_LABEL[f.service] ?? f.service}</Badge>
                  <span className="w-24 text-right font-medium text-ink">{eur(f.montantTtc)}</span>
                </label>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <span className="text-ink-muted">Total du rôle : </span>
                <span className="font-semibold text-ink">{eur(total)}</span>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void exporter()}
                disabled={exporting || selection.size === 0}
                style={{ background: ACCENT, borderColor: 'transparent' }}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {exporting ? 'Export…' : `Exporter le rôle (${selection.size})`}
              </Button>
            </div>

            <p className="mt-3 text-xs text-ink-subtle">
              Le fichier XML est conforme au schéma DGFiP. L'encapsulation PES_PJ, la signature et
              le dépôt sur le télé-service se feront au raccordement Hélios réel.
            </p>
          </Card>
        )
      )}
    </div>
  );
}

// ── Vue d'ensemble ───────────────────────────────────────────────────────────

function VueEnsemble({ dash, setTab }: { dash: ComptaDashboardData; setTab: (t: Tab) => void }) {
  const maxSrc = Math.max(1, ...dash.parSource.map((s) => s.montant));
  const kpis = [
    { label: 'Encaissé ce mois', value: eur(dash.encaisseMois), tone: 'good' as const, cible: 'encaissements' as const },
    { label: 'À valider (pointés)', value: dash.aValiderNb > 0 ? `${dash.aValiderNb} · ${eur(dash.aValiderMontant)}` : '0', tone: 'warn' as const, cible: 'encaissements' as const },
    { label: 'En attente d\'encaissement', value: eur(dash.enAttente), tone: 'warn' as const, cible: 'encaissements' as const },
    { label: 'Cautions détenues', value: eur(dash.cautionsDetenues), tone: 'accent' as const, cible: null },
    { label: 'À déposer en banque', value: eur(dash.aDeposer), tone: 'info' as const, cible: 'depots' as const },
  ];
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {kpis.map((k, i) => {
          const contenu = (
            <>
              <div className="flex items-center justify-between text-xs text-ink-subtle">
                <span>{k.label}</span>
                {k.cible && <ArrowRight className="h-3.5 w-3.5 text-ink-subtle" aria-hidden="true" />}
              </div>
              <div className="mt-1 text-2xl font-semibold tabular-nums text-ink">{k.value}</div>
            </>
          );
          return k.cible ? (
            <button
              key={i}
              type="button"
              onClick={() => setTab(k.cible!)}
              className="rounded-r-md border border-border bg-surface/40 p-3 text-left backdrop-blur-glass transition hover:border-ink-subtle hover:bg-surface/60"
              aria-label={`${k.label} — voir le détail`}
            >
              {contenu}
            </button>
          ) : (
            <div key={i} className="rounded-r-md border border-border bg-surface/40 p-3 backdrop-blur-glass">
              {contenu}
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card variant="glass" className="p-4">
          <div className="mb-3 font-display text-sm font-semibold text-ink">Recettes par source · ce mois</div>
          {dash.parSource.length === 0 ? (
            <div className="py-4 text-center text-sm text-ink-subtle">Aucune recette ce mois.</div>
          ) : (
            <div className="space-y-2.5">
              {dash.parSource.map((s, i) => {
                const m = srcMeta(s.source);
                return (
                  <div key={i}>
                    <div className="mb-1 flex justify-between text-xs">
                      <span className="flex items-center gap-1.5 text-ink-muted">
                        <span className="h-2 w-2 rounded-sm" style={{ background: m.color }} />
                        {m.label}
                      </span>
                      <span className="font-semibold text-ink">{eur(s.montant)}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-surface/60">
                      <div className="h-full rounded-full" style={{ width: `${(s.montant / maxSrc) * 100}%`, background: m.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card variant="glass" className="p-4">
          <div className="mb-3 font-display text-sm font-semibold text-ink">Trésorerie sur place</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-r-md border border-border bg-surface/40 p-3">
              <div className="text-[10px] uppercase tracking-wide text-ink-subtle">Espèces</div>
              <div className="mt-1 text-lg font-semibold tabular-nums text-ink">{eur(dash.tresorerie.especes)}</div>
            </div>
            <div className="rounded-r-md border border-border bg-surface/40 p-3">
              <div className="text-[10px] uppercase tracking-wide text-ink-subtle">Chèques</div>
              <div className="mt-1 text-lg font-semibold tabular-nums text-ink">{eur(dash.tresorerie.cheques)}</div>
            </div>
            <div className="rounded-r-md border p-3" style={{ borderColor: 'oklch(0.72 0.16 155 / 0.3)' }}>
              <div className="text-[10px] uppercase tracking-wide text-ink-subtle">Déposé ce mois</div>
              <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: 'oklch(0.72 0.16 155)' }}>{eur(dash.tresorerie.deposeMois)}</div>
            </div>
            <div className="rounded-r-md border p-3" style={{ borderColor: 'oklch(0.80 0.16 75 / 0.3)' }}>
              <div className="text-[10px] uppercase tracking-wide text-ink-subtle">Reste à déposer</div>
              <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: 'oklch(0.80 0.16 75)' }}>{eur(dash.aDeposer)}</div>
            </div>
          </div>
        </Card>
      </div>

      {dash.parPoste.length > 0 && (
        <Card variant="glass" className="mt-3 p-4">
          <div className="mb-3 font-display text-sm font-semibold text-ink">Ventilation par poste · cette année</div>
          <div className="space-y-1.5">
            {dash.parPoste.map((p) => (
              <div key={p.posteId} className="flex justify-between border-b border-border/60 py-1.5 text-sm last:border-0">
                <span className="text-ink-muted">{p.libelle}</span>
                <span className="font-semibold tabular-nums text-ink">{eur(p.montant)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

// ── Encaissements ────────────────────────────────────────────────────────────

async function validerProvisoire(id: string, onChange: () => Promise<void>, setError: (s: string | null) => void) {
  setError(null);
  try { await api.comptabilite.encaissements.valider(id); await onChange(); }
  catch (e) { setError(e instanceof ApiException ? e.message : 'Validation impossible'); }
}

function Encaissements({
  encs, postes, onChange, setError,
}: {
  encs: ComptaEncaissement[];
  postes: ComptaPoste[];
  onChange: () => Promise<void>;
  setError: (s: string | null) => void;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const nbProvisoires = encs.filter((e) => e.statut === 'provisoire').length;
  return (
    <>
      {nbProvisoires > 0 && (
        <div className="mb-3 rounded-r-lg border border-warn/40 bg-warn/10 px-3.5 py-2.5 text-xs text-ink-muted">
          <strong>{nbProvisoires} règlement(s) pointé(s)</strong> par l'accueil / les services, en attente de votre validation. Un encaissement pointé n'entre en dépôt et au journal qu'une fois <strong>validé</strong> ici.
        </div>
      )}
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-ink-muted">{encs.length} encaissement(s)</p>
        <Button type="button" variant="compta" size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Encaisser un règlement
        </Button>
      </div>

      {encs.length === 0 ? (
        <Card variant="glass" padded><p className="py-6 text-center text-sm text-ink-subtle">Aucun encaissement.</p></Card>
      ) : (
        <div className="space-y-2">
          {encs.map((e) => {
            const m = srcMeta(e.source);
            return (
              <Card key={e.id} variant="glass" className="p-3.5">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 h-full w-1 flex-shrink-0 self-stretch rounded-full" style={{ background: m.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-ink">{e.objet}</span>
                      <span className="rounded-r-sm px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: `color-mix(in oklab, ${m.color} 18%, transparent)`, color: m.color }}>{m.label}</span>
                      {e.statut === 'provisoire' && <Badge variant="warning" size="sm">À valider (pointé)</Badge>}
                      {e.statut === 'definitif' && !e.ventile && <Badge variant="warning" size="sm">À ventiler</Badge>}
                      {e.rapproche && <span className="text-[10px] text-success">Rapproché</span>}
                    </div>
                    <div className="mt-1 text-[11px] text-ink-subtle">
                      {MOYEN_LABEL[e.moyen]}{e.reference ? ` · ${e.reference}` : ''} · {new Date(e.dateEncaissement).toLocaleDateString('fr-FR')}
                      {e.depose ? ' · déposé' : ''}
                    </div>
                    {e.ventile && e.ventilation.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {e.ventilation.map((v, i) => (
                          <span key={i} className="rounded-r-sm bg-surface/60 px-1.5 py-0.5 text-[10px] text-ink-muted">
                            {v.posteLibelle} : {eur(v.montant)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="text-base font-semibold tabular-nums" style={{ color: 'oklch(0.72 0.16 155)' }}>+{eur(e.montant)}</div>
                    {e.statut === 'provisoire' && (
                      <Button type="button" variant="compta" size="sm" className="mt-1.5" onClick={() => void validerProvisoire(e.id, onChange, setError)}>
                        Valider l'encaissement
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {showCreate && (
        <EncaissementForm
          postes={postes}
          onClose={() => setShowCreate(false)}
          onSaved={async () => { setShowCreate(false); await onChange(); }}
          setError={setError}
        />
      )}
    </>
  );
}

function EncaissementForm({
  postes, onClose, onSaved, setError,
}: {
  postes: ComptaPoste[];
  onClose: () => void;
  onSaved: () => Promise<void>;
  setError: (s: string | null) => void;
}) {
  const [objet, setObjet] = useState('');
  const [montant, setMontant] = useState('');
  const [moyen, setMoyen] = useState<string>('cheque');
  const [source, setSource] = useState<string>('divers');
  const [reference, setReference] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [saving, setSaving] = useState(false);
  // LOT 2 — encaissement relié : une facture émise peut être sélectionnée pour
  // pré-remplir objet/montant/référence (fin de la saisie libre de référence).
  const [factureQuery, setFactureQuery] = useState('');
  const [factures, setFactures] = useState<FactureAEncaisser[]>([]);
  const [factureId, setFactureId] = useState<string | null>(null);
  const [openFactures, setOpenFactures] = useState(false);
  useEffect(() => {
    let stop = false;
    const t = setTimeout(async () => {
      try { const r = await api.comptabilite.facturesManuelles.aEncaisser(factureQuery || undefined); if (!stop) setFactures(r.factures); }
      catch { if (!stop) setFactures([]); }
    }, 200);
    return () => { stop = true; clearTimeout(t); };
  }, [factureQuery]);

  function choisirFacture(f: FactureAEncaisser) {
    setFactureId(f.id);
    setObjet(f.objet || `Facture ${f.numero}`);
    setMontant(String(f.montantTtc).replace('.', ','));
    setReference(f.numero);
    setOpenFactures(false);
    setFactureQuery(f.numero);
  }
  function resetFacture() {
    setFactureId(null); setReference(''); setFactureQuery('');
  }
  // Ventilation : posteId -> montant (string)
  const [vent, setVent] = useState<Record<string, string>>({});

  const montantNum = parseFloat(montant.replace(',', '.')) || 0;
  const sommeVent = Object.values(vent).reduce((s, v) => s + (parseFloat(v.replace(',', '.')) || 0), 0);
  const ventLignes = Object.entries(vent)
    .map(([posteId, v]) => ({ posteId, montant: parseFloat(v.replace(',', '.')) || 0 }))
    .filter((l) => l.montant > 0);
  const ventActive = ventLignes.length > 0;
  const ventOk = !ventActive || Math.abs(sommeVent - montantNum) < 0.005;

  const submit = async () => {
    if (!objet.trim() || montantNum <= 0) return;
    if (ventActive && !ventOk) {
      setError(`La ventilation (${sommeVent.toFixed(2)} €) doit égaler le montant (${montantNum.toFixed(2)} €).`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.comptabilite.encaissements.create({
        objet: objet.trim(),
        montant: montantNum,
        moyen,
        source,
        reference: reference.trim() || null,
        commentaire: commentaire.trim() || null,
        factureId: factureId ?? undefined,
        ventilation: ventActive ? ventLignes : undefined,
      });
      await onSaved();
    } catch {
      setError('L\'encaissement n\'a pas pu être enregistré.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} title="Encaisser un règlement" size="lg">
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted">Facture à encaisser <span className="text-ink-subtle">(recommandé)</span></label>
          <div className="relative">
            <input
              value={factureQuery}
              onChange={(e) => { setFactureQuery(e.target.value); setOpenFactures(true); if (factureId) resetFacture(); }}
              onFocus={() => setOpenFactures(true)}
              placeholder="Rechercher par n°, client ou montant…"
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:outline-none" />
            {openFactures && factures.length > 0 && !factureId && (
              <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-r-md border border-border-hi bg-surface shadow-r-lg">
                {factures.map((f) => (
                  <button key={f.id} type="button" onClick={() => choisirFacture(f)}
                    className="flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2 text-left last:border-0 hover:bg-surface2/60">
                    <span className="min-w-0"><span className="text-xs font-semibold text-ink">{f.numero}</span> <span className="text-xs text-ink-muted">· {f.clientNom || '—'}</span><br /><span className="text-[11px] text-ink-subtle">{f.objet || ''}</span></span>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-ink">{f.montantTtc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {factureId && <p className="mt-1 text-[11px] text-compta">Reliée à {reference} — montant et référence verrouillés. <button type="button" onClick={resetFacture} className="underline">Changer</button></p>}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="enc-objet">Objet / motif</label>
          <input id="enc-objet" value={objet} onChange={(e) => setObjet(e.target.value)}
            placeholder="Dégradation salle, recette diverse, devis…"
            className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="enc-montant">Montant (€)</label>
            <input id="enc-montant" value={montant} onChange={(e) => setMontant(e.target.value)} inputMode="decimal" placeholder="300,00"
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="enc-moyen">Moyen</label>
            <select id="enc-moyen" value={moyen} onChange={(e) => setMoyen(e.target.value)}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              {MOYENS.map((m) => <option key={m} value={m}>{MOYEN_LABEL[m]}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="enc-source">Secteur</label>
            <select id="enc-source" value={source} onChange={(e) => setSource(e.target.value)}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              {SOURCES.map((s) => <option key={s} value={s}>{srcMeta(s).label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="enc-ref">Référence</label>
            <input id="enc-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="DEV-2026-0007"
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="enc-com">Commentaire</label>
          <input id="enc-com" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} placeholder="Réglé suite au devis…"
            className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
        </div>

        {/* Ventilation */}
        {postes.length > 0 && (
          <div className="rounded-r-md border border-border bg-surface/30 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-ink">Ventilation (optionnelle)</span>
              {ventActive && (
                <span className={`text-[11px] font-medium ${ventOk ? 'text-success' : 'text-urgent'}`}>
                  {sommeVent.toFixed(2)} € / {montantNum.toFixed(2)} €
                </span>
              )}
            </div>
            <div className="space-y-1.5">
              {postes.filter((p) => p.actif).map((p) => (
                <div key={p.id} className="flex items-center gap-2">
                  <span className="flex-1 text-sm text-ink-muted">{p.libelle}</span>
                  <input
                    value={vent[p.id] ?? ''}
                    onChange={(e) => setVent((prev) => ({ ...prev, [p.id]: e.target.value }))}
                    inputMode="decimal"
                    placeholder="0"
                    className="w-24 rounded-r-sm border border-border-hi bg-surface/80 px-2 py-1 text-right text-sm text-ink focus:outline-none"
                  />
                  <span className="text-xs text-ink-subtle">€</span>
                </div>
              ))}
            </div>
            {!ventActive && <p className="mt-2 text-[11px] text-ink-subtle">Laisser vide = non ventilé (à ventiler plus tard).</p>}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
          <Button type="button" variant="compta" size="sm" onClick={() => void submit()} disabled={saving || !objet.trim() || montantNum <= 0 || (ventActive && !ventOk)}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Dépôts en banque ─────────────────────────────────────────────────────────

function Depots({
  encs, depots, onChange, setError,
}: {
  encs: ComptaEncaissement[];
  depots: ComptaDepot[];
  onChange: () => Promise<void>;
  setError: (s: string | null) => void;
}) {
  const aDeposer = encs.filter((e) => !e.depose && (e.moyen === 'cheque' || e.moyen === 'espece'));
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const ids = aDeposer.filter((e) => selected[e.id]).map((e) => e.id);
  const total = aDeposer.filter((e) => selected[e.id]).reduce((s, e) => s + e.montant, 0);

  const submit = async () => {
    if (ids.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await api.comptabilite.depots.create(ids);
      setSelected({});
      await onChange();
    } catch {
      setError('Le dépôt n\'a pas pu être créé.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <Card variant="glass" className="p-4">
        <div className="mb-3 font-display text-sm font-semibold text-ink">Préparer un dépôt</div>
        {aDeposer.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-subtle">Rien à déposer (chèques / espèces).</p>
        ) : (
          <>
            <div className="space-y-1.5">
              {aDeposer.map((e) => (
                <label key={e.id} className="flex cursor-pointer items-center gap-2 rounded-r-sm border border-border/60 bg-surface/40 px-2.5 py-2">
                  <input type="checkbox" checked={!!selected[e.id]} onChange={(ev) => setSelected((p) => ({ ...p, [e.id]: ev.target.checked }))} className="h-4 w-4" />
                  <span className="flex-1 text-sm text-ink">{e.objet}</span>
                  <span className="text-[11px] text-ink-subtle">{MOYEN_LABEL[e.moyen]}</span>
                  <span className="text-sm font-semibold tabular-nums text-ink">{eur(e.montant)}</span>
                </label>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
              <span className="text-sm text-ink-muted">Total sélectionné</span>
              <span className="text-lg font-semibold tabular-nums text-ink">{eur(total)}</span>
            </div>
            <Button type="button" variant="compta" size="sm" className="mt-3 w-full" onClick={() => void submit()} disabled={saving || ids.length === 0}>
              {saving ? 'Création…' : 'Valider le dépôt + bordereau'}
            </Button>
          </>
        )}
      </Card>

      <Card variant="glass" className="p-4">
        <div className="mb-3 font-display text-sm font-semibold text-ink">Bordereaux émis</div>
        {depots.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-subtle">Aucun dépôt enregistré.</p>
        ) : (
          <div className="space-y-2">
            {depots.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-r-sm border border-border/60 bg-surface/40 px-3 py-2">
                <FileDown className="h-4 w-4 text-ink-muted" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-ink">{d.numeroBordereau}</div>
                  <div className="text-[11px] text-ink-subtle">{new Date(d.dateDepot + 'T00:00:00').toLocaleDateString('fr-FR')} · {d.nbCheques} chèque(s), {d.nbEspeces} espèce(s)</div>
                </div>
                <span className="text-sm font-semibold tabular-nums text-ink">{eur(d.montantTotal)}</span>
                <button
                  type="button"
                  title="Télécharger le bordereau (PDF)"
                  onClick={() => downloadBlob(() => api.comptabilite.depots.bordereauBlob(d.id), `bordereau-${d.numeroBordereau}.pdf`).catch(() => setError('Téléchargement impossible.'))}
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-r-md border border-border text-ink-muted transition hover:bg-surface hover:text-ink"
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ── Postes de ventilation ────────────────────────────────────────────────────

function Postes({
  postes, onChange, setError,
}: {
  postes: ComptaPoste[];
  onChange: () => Promise<void>;
  setError: (s: string | null) => void;
}) {
  const [libelle, setLibelle] = useState('');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!libelle.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.comptabilite.postes.create({ libelle: libelle.trim() });
      setLibelle('');
      await onChange();
    } catch {
      setError('Le poste n\'a pas pu être créé.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActif = async (p: ComptaPoste) => {
    try {
      await api.comptabilite.postes.update(p.id, { actif: !p.actif });
      await onChange();
    } catch {
      setError('Modification impossible.');
    }
  };

  return (
    <Card variant="glass" className="p-4">
      <div className="mb-1 font-display text-sm font-semibold text-ink">Postes de ventilation</div>
      <p className="mb-3 text-xs text-ink-subtle">
        Votre nomenclature comptable libre. Ces postes apparaissent dans le formulaire d'encaissement pour ventiler les recettes.
      </p>
      <div className="mb-4 flex gap-2">
        <input
          value={libelle}
          onChange={(e) => setLibelle(e.target.value)}
          placeholder="Ex. Régie fêtes, Recettes voirie…"
          className="flex-1 rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:outline-none"
        />
        <Button type="button" variant="compta" size="sm" onClick={() => void add()} disabled={saving || !libelle.trim()}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Ajouter
        </Button>
      </div>
      {postes.length === 0 ? (
        <p className="py-4 text-center text-sm text-ink-subtle">Aucun poste créé.</p>
      ) : (
        <div className="space-y-1.5">
          {postes.map((p) => (
            <div key={p.id} className={`flex items-center gap-2 rounded-r-sm border border-border/60 px-3 py-2 ${p.actif ? 'bg-surface/40' : 'bg-surface/20 opacity-60'}`}>
              <span className="flex-1 text-sm text-ink">{p.libelle}</span>
              {!p.actif && <Badge variant="neutral" size="sm">Inactif</Badge>}
              <button type="button" onClick={() => void toggleActif(p)} className="text-xs text-ink-muted underline hover:text-ink">
                {p.actif ? 'Désactiver' : 'Réactiver'}
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Journal ──────────────────────────────────────────────────────────────────

function Journal({ setError }: { setError: (s: string | null) => void }) {
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [data, setData] = useState<ComptaJournalResult | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.comptabilite.journal.list({ du: du || undefined, au: au || undefined });
      setData(r);
    } catch {
      setError('Impossible de charger le journal.');
    } finally {
      setLoading(false);
    }
  }, [du, au, setError]);

  useEffect(() => { void load(); }, [load]);

  const toggleRapproche = async (l: ComptaJournalResult['lignes'][number]) => {
    try {
      await api.comptabilite.journal.rapprocher(l.id, !l.rapproche);
      await load();
    } catch {
      setError('Action impossible.');
    }
  };

  const filtre = { du: du || undefined, au: au || undefined };

  return (
    <>
      {/* Barre filtres + exports */}
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="j-du">Du</label>
          <input id="j-du" type="date" value={du} onChange={(e) => setDu(e.target.value)}
            className="rounded-r-md border border-border-hi bg-surface/80 px-2.5 py-1.5 text-sm text-ink focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="j-au">Au</label>
          <input id="j-au" type="date" value={au} onChange={(e) => setAu(e.target.value)}
            className="rounded-r-md border border-border-hi bg-surface/80 px-2.5 py-1.5 text-sm text-ink focus:outline-none" />
        </div>
        {(du || au) && (
          <Button type="button" variant="ghost" size="sm" onClick={() => { setDu(''); setAu(''); }}>Réinitialiser</Button>
        )}
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="secondary" size="sm"
            onClick={() => downloadBlob(() => api.comptabilite.journal.csvBlob(filtre), 'journal-comptable.csv').catch(() => setError('Export CSV impossible.'))}>
            <Download className="h-4 w-4" aria-hidden="true" />
            CSV
          </Button>
          <Button type="button" variant="secondary" size="sm"
            onClick={() => downloadBlob(() => api.comptabilite.journal.pdfBlob(filtre), 'grand-livre.pdf').catch(() => setError('Export PDF impossible.'))}>
            <FileText className="h-4 w-4" aria-hidden="true" />
            Grand-livre PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <Card variant="glass" padded><div className="py-8 text-center text-sm text-ink-subtle">Chargement…</div></Card>
      ) : !data || data.lignes.length === 0 ? (
        <Card variant="glass" padded><p className="py-6 text-center text-sm text-ink-subtle">Aucune opération sur la période.</p></Card>
      ) : (
        <Card variant="glass" className="overflow-hidden p-0">
          {/* Totaux */}
          <div className="flex flex-wrap gap-4 border-b border-border bg-surface/30 px-4 py-3 text-sm">
            <span className="text-ink-muted">{data.nbOperations} opération(s)</span>
            <span className="text-ink-muted">Total : <span className="font-semibold text-ink">{eur(data.totalEncaisse)}</span></span>
            <span className="text-ink-muted">Rapproché : <span className="font-semibold" style={{ color: 'oklch(0.72 0.16 155)' }}>{eur(data.totalRapproche)}</span></span>
          </div>
          {/* Lignes */}
          <div className="divide-y divide-border/60">
            {data.lignes.map((l) => {
              const m = srcMeta(l.source);
              return (
                <div key={l.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="h-8 w-1 flex-shrink-0 rounded-full" style={{ background: m.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-ink">{l.objet}</span>
                      {!l.ventilation.length && <Badge variant="warning" size="sm">À ventiler</Badge>}
                    </div>
                    <div className="text-[11px] text-ink-subtle">
                      {new Date(l.date).toLocaleDateString('fr-FR')} · {MOYEN_LABEL[l.moyen]}{l.reference ? ` · ${l.reference}` : ''}{l.depose ? ' · déposé' : ''}
                    </div>
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-ink">{eur(l.montant)}</span>
                  <label className="flex flex-shrink-0 cursor-pointer items-center gap-1.5" title="Rapprochement bancaire">
                    <input type="checkbox" checked={l.rapproche} onChange={() => void toggleRapproche(l)} className="h-4 w-4" />
                    <span className="text-[10px] text-ink-subtle">Pointé</span>
                  </label>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </>
  );
}
